import type { AttributeInfo } from 'cadenza/query-language/attribute-info';
import type { AttributeType } from 'cadenza/repository/attribute-type';
import type { QlMode } from 'cadenza/repository/model/ql-condition';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { QlConditionVariableDto } from 'cadenza/workbook/model/ql-condition-variable-dto';

export interface Context {
  /**
   * Attributes that are eligible to be used and offered to the user via menus and suggestions, and selectable by them.
   * Note: include attributes with `visibleInDataBrowser === false` even if the UI hides them as they should be usable.
   */
  usableAttributes: AttributeContext;
  /**
   * All attributes that may be encountered when processing CQL from existing calculated attributes, including
   * - attributes from visible-restricted (hidden) objecttypes (for CQL conditions) and
   * - attributes with incompatible attribute types (for CQL attributes).
   *
   * Required to handle calculated attributes whose referenced attributes have been modified in the management center afterward.
   */
  allAttributes: AttributeContext;
  variables: VariableContext;
  mode: QlMode;
}

export type AttributeContext = Record<string, Record<string, AttributeInfo>>;
export type VariableContext = QlConditionVariableDto[];

const SUPPORTED_TYPES: Set<AttributeType> = new Set(['STRING', 'DATE', 'BIGDECIMAL', 'DOUBLE', 'INT', 'LONG']);

export function createAttributesContext(attributes: WorkbookAttributeDto[]): AttributeContext {
  return attributes.reduce((context: AttributeContext, attribute) => {
    if (isSupportedInParser(attribute)) {
      const objecttypePrintName = attribute.objecttypePrintName;
      if (!context[objecttypePrintName]) {
        context[objecttypePrintName] = {};
      }
      context[objecttypePrintName][attribute.printName] = {
        format: attribute.format,
        type: attribute.type,
        attributeId: attribute.baseId || (attribute.id as AttributeId),
        usableInCondition: attribute.usableInCondition,
        visibleInDataBrowser: attribute.visibleInDataBrowser,
      };
    }
    return context;
  }, {});
}

export function createVariablesContext(variable: QlConditionVariableDto): VariableContext {
  return variable ? [variable] : [];
}

function isSupportedInParser(attribute: WorkbookAttributeDto) {
  return SUPPORTED_TYPES.has(attribute.type);
}
